import React, { useEffect, useMemo, useState } from "react";
import { Course, Player, ScoreMap } from "./types";
import { ActiveGame } from "./activeRound";
import { GroupScores, fetchTournamentScores } from "./tournamentPlay";
import LeaderboardModal from "./modals/LeaderboardModal";
import Scorecard from "./Scorecard";

interface Props {
  tournamentId: string;
  course: Course;
  // The tournament's games (scored across every group).
  games: ActiveGame[];
  title: string;
  subtitle?: string;
  // Tee whose yardages show on the combined card. Optional — falls back to the
  // course default inside Scorecard.
  displayTeeId?: string;
  // Optional "My Group" data (player History). When provided, the leaderboard
  // shows the My Group | Tournament toggle (defaulting to My Group) instead of
  // being tournament-only — the player can see their own group's side-game
  // results as well as the cross-group standings. Admin View Scores omits these
  // (there's no "my group" for an admin), so it stays tournament-only.
  localPlayers?: Player[];
  localScores?: ScoreMap;
  localGames?: ActiveGame[];
  tournamentGroupId?: string;
  tournamentGroupName?: string;
  // Pre-fetched group scores. When the caller already loaded them (admin
  // View Scores loads behind a button spinner), pass them here so the results
  // render fully on open instead of showing an in-modal loading state.
  initialGroups?: GroupScores[];
  onClose: () => void;
}

const noop = () => {};

// Read-only tournament results: the cross-group Leaderboard plus a single
// scorecard that STACKS every group's players onto one card. Used by the admin
// "View Scores" action and by the player History view for a tournament round.
// Mirrors the History leaderboard ↔ scorecard flow, but always tournament-wide.
export default function TournamentResults({
  tournamentId,
  course,
  games,
  title,
  subtitle,
  displayTeeId,
  localPlayers,
  localScores,
  localGames,
  tournamentGroupId,
  tournamentGroupName,
  initialGroups,
  onClose,
}: Props) {
  const hasLocal = !!localPlayers;
  const [mode, setMode] = useState<"leaderboard" | "scorecard">("leaderboard");
  // Every group's submitted players + scores, merged into one field for the
  // combined card. Seeded from initialGroups (caller pre-fetch) so the card is
  // ready immediately; the effect still refreshes in the background.
  const [groups, setGroups] = useState<GroupScores[]>(initialGroups ?? []);
  useEffect(() => {
    let alive = true;
    void (async () => {
      const g = await fetchTournamentScores(tournamentId);
      if (alive) setGroups(g);
    })();
    return () => {
      alive = false;
    };
  }, [tournamentId]);

  // Flatten every group's players into one card, recording where each group
  // starts so the Scorecard can draw a "Group 1 · 2:00" divider before it.
  const { players, groupBreaks } = useMemo(() => {
    const flat: Player[] = [];
    const breaks: { startIndex: number; label: string }[] = [];
    for (const g of groups) {
      if (g.players.length === 0) continue;
      const name = g.groupName || "Group";
      breaks.push({
        startIndex: flat.length,
        label: g.teeTime ? `${name} · ${g.teeTime}` : name,
      });
      flat.push(...g.players);
    }
    return { players: flat, groupBreaks: breaks };
  }, [groups]);
  const scores: ScoreMap = useMemo(() => {
    const m: ScoreMap = {};
    for (const g of groups) Object.assign(m, g.scores);
    return m;
  }, [groups]);

  const totalPar = course.holes.reduce((s, h) => s + h.par, 0);

  if (mode === "scorecard") {
    return (
      <Scorecard
        course={course}
        players={players}
        scores={scores}
        displayTeeId={displayTeeId}
        onPlayersChange={noop}
        onScoresChange={noop}
        onDisplayTeeChange={noop}
        onChangeCourse={() => setMode("leaderboard")}
        onEndRound={noop}
        readOnly
        groupBreaks={groupBreaks}
      />
    );
  }

  return (
    <LeaderboardModal
      players={localPlayers ?? []}
      scores={localScores ?? {}}
      games={localGames}
      totalPar={totalPar}
      course={course}
      tournamentGames={games}
      tournamentId={tournamentId}
      tournamentGroupId={tournamentGroupId}
      tournamentGroupName={tournamentGroupName}
      tournamentOnly={!hasLocal}
      initialFieldGroups={initialGroups}
      title={title}
      subtitle={subtitle}
      onViewScorecard={() => setMode("scorecard")}
      onClose={onClose}
    />
  );
}
